import { createElement, type ReactNode } from 'react';

/**
 * Tiny, dependency-free Markdown renderer for long-form consent text (e.g. terms
 * & conditions). Renders to React elements only — never raw HTML — so authored
 * content can't inject markup. Supports headings (#…######), paragraphs, ordered
 * and unordered lists, horizontal rules, and inline **bold** / *italic* / `code`
 * / [links](url) (safe protocols only).
 */

/** Permit only safe link targets; anything else renders as plain text. */
function safeHref(url: string): string | undefined {
  const u = url.trim();
  if (/^(https?:|mailto:)/i.test(u)) return u;
  if (u.startsWith('/') || u.startsWith('#')) return u;
  return undefined;
}

const INLINE =
  /\*\*([^*]+)\*\*|__([^_]+)__|\*([^*]+)\*|_([^_]+)_|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)/;

/** Parse inline markup within a single block of text into React nodes. */
function renderInline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  let i = 0;
  while (rest) {
    const m = INLINE.exec(rest);
    if (!m) {
      out.push(rest);
      break;
    }
    if (m.index > 0) out.push(rest.slice(0, m.index));
    const k = `${keyBase}-${i++}`;
    if (m[1] ?? m[2]) out.push(<strong key={k}>{m[1] ?? m[2]}</strong>);
    else if (m[3] ?? m[4]) out.push(<em key={k}>{m[3] ?? m[4]}</em>);
    else if (m[5]) out.push(<code key={k}>{m[5]}</code>);
    else if (m[6]) {
      const href = safeHref(m[7] ?? '');
      out.push(
        href ? (
          <a key={k} href={href} target="_blank" rel="noreferrer noopener">
            {m[6]}
          </a>
        ) : (
          m[6]
        ),
      );
    }
    rest = rest.slice(m.index + m[0].length);
  }
  return out;
}

export function Markdown({ text, className }: { text: string; className?: string }) {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let para: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let key = 0;

  const flushPara = () => {
    if (!para.length) return;
    const id = key++;
    blocks.push(<p key={`p${id}`}>{renderInline(para.join(' '), `p${id}`)}</p>);
    para = [];
  };
  const flushList = () => {
    if (!list) return;
    const id = key++;
    const { ordered, items } = list;
    const lis = items.map((it, idx) => <li key={idx}>{renderInline(it, `l${id}-${idx}`)}</li>);
    blocks.push(ordered ? <ol key={`l${id}`}>{lis}</ol> : <ul key={`l${id}`}>{lis}</ul>);
    list = null;
  };
  const flush = () => {
    flushPara();
    flushList();
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flush();
      continue;
    }
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      flush();
      const id = key++;
      const level = Math.min((heading[1] ?? '').length + 2, 6); // # → h3, ###### → h6
      blocks.push(createElement(`h${level}`, { key: `h${id}` }, renderInline(heading[2] ?? '', `h${id}`)));
      continue;
    }
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line)) {
      flush();
      blocks.push(<hr key={`hr${key++}`} />);
      continue;
    }
    const ul = /^[-*+]\s+(.*)$/.exec(line);
    if (ul) {
      flushPara();
      if (!list || list.ordered) {
        flushList();
        list = { ordered: false, items: [] };
      }
      list.items.push(ul[1] ?? '');
      continue;
    }
    const ol = /^\d+\.\s+(.*)$/.exec(line);
    if (ol) {
      flushPara();
      if (!list || !list.ordered) {
        flushList();
        list = { ordered: true, items: [] };
      }
      list.items.push(ol[1] ?? '');
      continue;
    }
    flushList();
    para.push(line);
  }
  flush();

  return <div className={className}>{blocks}</div>;
}
